import { Component } from "react";
import { graphql } from "gatsby";
import styles from "./toolbar-block.module.less";
import OpeningHoursStore from "components/stores/opening-hours-store";
import { createDate, DateHelper, extract, EventAggregator, Icon } from "@parksandresorts/core";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import SpeechBubbleShow from "components/block-elements/speech-bubble-show/speech-bubble-show";
import SpeechBubbleGeneral from "components/block-elements/speech-bubble-general/speech-bubble-general";

export default class ToolbarBlock extends Component {
  constructor(props) {
    super(props);

    this.link = extract(props, "link");
    this.internalLink = extract(this.link, "internalLink.slug");
    this.today = createDate();
    this.openTodayLabel = extract(props, "openTodayLabel");
    this.closedLabel = extract(props, "closedTodayLabel");
    this.nextDateLabel = extract(props, "nextDateLabel");
    this.toolbarLinks = extract(props, "toolbarLinks");
    this.calendarLink = extract(props, "calendarLink.slug");
    this.calendarLabel = extract(props, "calendarLabel");
    this.customOpeningHoursText = extract(props, "customOpeningHoursText");
    this.showSpeechBubble = extract(props, "showSpeechBubble");
    this.speechBubblePosition = extract(props, "speechBubblePosition");
    this.bubbleBelongsToItem = extract(props, "belongsToItem");
    this.speechBubbleContent = extract(props, "speechBubbleContent[0]");

    this.state = {
      openingHoursForToday: null,
      closestOpeningHours: null,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    OpeningHoursStore.getAllDates().then((response) => {
      if (!this._isMounted) {
        return;
      }

      const openingHoursForToday = this.findOpeningHoursForDate(response, this.today);
      if (openingHoursForToday) {
        this.setState({
          openingHoursForToday: openingHoursForToday,
        });
      } else {
        const closestOpeningHours = this.findNextOpenDay(response);
        this.setState({
          closestOpeningHours: closestOpeningHours,
        });
      }
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  findOpeningHoursForDate(response, selectedDate) {
    const date = response.find((item) =>
      item.areaOpeningHours.find(
        (area) => area.areaId === 1 && DateHelper.isEqualYYYYMMDD(area.dateFrom, selectedDate.toISOString()),
      ),
    );

    return date;
  }

  findNextOpenDay(response) {
    if (!(response && response instanceof Array)) {
      return null;
    }

    for (const dateItem of response) {
      const openingHoursForArea = this.getAreaOpenHours(dateItem);

      if (openingHoursForArea) {
        const openingDate = createDate(openingHoursForArea.dateFrom);

        if (openingDate > this.today) {
          return dateItem;
        }
      }
    }

    return null;
  }

  getAreaOpenHours(openingHours) {
    return openingHours.areaOpeningHours.find((area) => area.areaId === 1);
  }

  renderOpeningHoursArea() {
    return (
      <LinkWrapper
        url={this.calendarLink}
        onPreNavigate={() =>
          EventAggregator.publish("trackEvent", { category: "Toolbar", action: "Clicked link", label: "Kalender" })
        }
      >
        <div className={[styles.openTodayWrapper, "p_small"].join(" ")}>
          {this.state.openingHoursForToday
            ? this.renderOpenToday()
            : this.state.closestOpeningHours
            ? this.renderClosedTodayOpensAnotherDate()
            : this.renderClosedToday()}
        </div>
      </LinkWrapper>
    );
  }

  renderOpenToday() {
    const areaHours = this.getAreaOpenHours(this.state.openingHoursForToday);
    const opens = createDate(areaHours.dateFrom);
    const closes = createDate(areaHours.dateTo);
    return (
      <>
        <div className={styles.calendarInfoWrapper}>
          <Icon name="calendar" className={styles.calendarIcon} />
          <span className={[styles.label].join(" ")}>
            <span className={styles.openTodayText}>{this.openTodayLabel}</span>
            <span>
              {DateHelper.toStringTimeHHMM(opens)} - {DateHelper.toStringTimeHHMM(closes)}
            </span>
          </span>
        </div>
        <span className={styles.calendarLink}>
          {this.calendarLabel && this.calendarLabel} <Icon name="chevron-right" size="xxs" />
        </span>
      </>
    );
  }

  renderClosedToday() {
    return (
      <>
        <div className={styles.calendarInfoWrapper}>
          <Icon name="calendar" className={styles.calendarIcon} />
          <span className={[styles.label].join(" ")}>
            <span className={styles.openTodayText}>{this.closedLabel}</span>
          </span>
        </div>
        <span className={styles.calendarLink}>
          {this.calendarLabel && this.calendarLabel} <Icon name="chevron-right" size="xxs" />
        </span>
      </>
    );
  }

  renderClosedTodayOpensAnotherDate() {
    const areaHours = this.getAreaOpenHours(this.state.closestOpeningHours);
    const opens = createDate(areaHours.dateFrom);

    return (
      <>
        <div className={styles.calendarInfoWrapper}>
          <Icon name="calendar" className={styles.calendarIcon} />
          <span className={[styles.label].join(" ")}>
            <span className={styles.openTodayText}>{this.closedLabel}</span>
          </span>
        </div>
        <span className={styles.calendarLink}>
          {this.customOpeningHoursText
            ? this.customOpeningHoursText
            : this.nextDateLabel + " " + DateHelper.toStringDDShortMonth(opens)}{" "}
          <Icon name="chevron-right" size="xxs" />
        </span>
      </>
    );
  }

  renderSpeechBubble() {
    if (!this.speechBubbleContent) {
      return null;
    }

    switch (this.speechBubbleContent.__typename) {
      case "ContentfulShowBlock":
        return (
          <SpeechBubbleShow
            bubbleBelongsTo={this.bubbleBelongsToItem ? this.bubbleBelongsToItem : undefined}
            poisitionOver={this.speechBubblePosition ? this.speechBubblePosition : undefined}
            {...this.speechBubbleContent}
          />
        );
      case "ContentfulGeneralContentBlock":
        return (
          <SpeechBubbleGeneral
            bubbleBelongsTo={this.bubbleBelongsToItem ? this.bubbleBelongsToItem : undefined}
            poisitionOver={this.speechBubblePosition ? this.speechBubblePosition : undefined}
            {...this.speechBubbleContent}
          />
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <section
        className={[`block block--no-margin ${styles.background}`].join(" ")}
        data-contentful-id={this.props.contentful_id}
      >
        <div className={[styles.wrapper].join(" ")}>
          <div className={styles.linksAndBubbleWrapper}>
            <ul className={styles.linkWrapper}>
              {this.toolbarLinks instanceof Array &&
                this.toolbarLinks.length > 0 &&
                this.toolbarLinks.map((item) => {
                  return (
                    <li key={item.id} className={styles.linkItem}>
                      <LinkWrapper
                        className={styles.link}
                        url={item.internalLink ? item.internalLink.slug : undefined}
                        onPreNavigate={() =>
                          EventAggregator.publish("trackEvent", {
                            category: "Toolbar",
                            action: "Clicked link",
                            label: item.title,
                          })
                        }
                      >
                        {item.linkIcon && <span className={[styles.linkIcon, item.linkIcon].join(" ")}></span>}
                        <span className={[styles.linkLabel, styles.linkLabel__largeDevice, "body-s"].join(" ")}>
                          {item.title}
                        </span>
                        <span className={[styles.linkLabel, styles.linkLabel__smallDevice, "body-s"].join(" ")}>
                          {item.smallDeviceTitle ? item.smallDeviceTitle : item.title}
                        </span>
                      </LinkWrapper>
                    </li>
                  );
                })}
            </ul>
            {this.showSpeechBubble && <div className={styles.speechBubbleWrapper}>{this.renderSpeechBubble()}</div>}
          </div>
          {this.calendarLink && this.renderOpeningHoursArea()}
        </div>
      </section>
    );
  }
}

export const fragmentToolbarBlock = graphql`
  fragment ToolbarBlock on ContentfulToolbarBlock {
    contentful_id
    id
    toolbarLinks {
      ...Link
    }
    calendarLink {
      slug
    }
    calendarLabel
    openTodayLabel
    closedTodayLabel
    nextDateLabel
    customOpeningHoursText
    showSpeechBubble
    belongsToItem
    speechBubblePosition
    speechBubbleContent {
      __typename
      ... on ContentfulShowBlock {
        id
        title
        imageInList {
          file {
            url
          }
        }
        pageLink {
          slug
        }
      }
      ... on ContentfulGeneralContentBlock {
        id
        title
        componentTheme
        speechBubbleInverseColors
        imageInBubble {
          file {
            url
          }
        }
        speechBubbleLink {
          internalLink {
            slug
          }
          externalUrl
        }
      }
    }
  }
`;
